import React, { useState } from 'react';
import { Box, Container, Typography, Grid, TextField, Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EmailIcon from '@mui/icons-material/Email';
import { sendEmail } from '../utils/emailService';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#F9FAFB', borderRadius: '10px',
    '& fieldset': { borderColor: '#E5E7EB' },
    '&:hover fieldset': { borderColor: 'rgba(99,102,241,0.4)' },
    '&.Mui-focused fieldset': { borderColor: '#6366F1', borderWidth: '1px' },
  },
  '& .MuiInputLabel-root': { color: '#9CA3AF' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#6366F1' },
  '& .MuiOutlinedInput-input': { color: '#0F1117', fontSize: '0.95rem' },
};

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    const result = await sendEmail(formData, 'General Contact');
    setSending(false);
    if (result.success) {
      alert("Message sent! We'll get back to you shortly.");
      setFormData({ name: '', email: '', company: '', message: '' });
    } else {
      alert('Failed to send. Please email us at admin@infynihub.com');
    }
  };

  const set = (f) => (e) => setFormData({ ...formData, [f]: e.target.value });

  return (
    <Box id="contact" sx={{ bgcolor: '#F8F9FF', py: { xs: 7, md: 14 }, position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', bottom: '-15%', left: '-8%', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 65%)', pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', top: '-10%', right: '-5%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(129,140,248,0.05) 0%, transparent 65%)', pointerEvents: 'none' }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center">
          {/* Left */}
          <Grid item xs={12} md={5}>
            <Typography sx={{ color: '#6366F1', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', mb: 2 }}>Get In Touch</Typography>
            <Typography sx={{ fontWeight: 900, color: '#0F1117', fontSize: { xs: '2rem', sm: '2.4rem', md: '3rem' }, lineHeight: 1.1, letterSpacing: '-0.02em', mb: 3 }}>
              Let's talk about{' '}
              <Box component="span" sx={{ color: '#6366F1' }}>your business.</Box>
            </Typography>
            <Typography sx={{ color: '#6B7280', lineHeight: 1.85, mb: 5, fontSize: '0.95rem' }}>
              Tell us what you're working with today. We'll come back with a practical recommendation — no sales pitch, no obligation.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2.5, bgcolor: '#FFFFFF', borderRadius: '14px', border: '1px solid #E5E7EB' }}>
              <Box sx={{ width: 44, height: 44, borderRadius: '10px', bgcolor: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6366F1', flexShrink: 0 }}>
                <EmailIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box>
                <Typography sx={{ color: '#6B7280', fontSize: '0.72rem', mb: 0.25, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Email</Typography>
                <Typography sx={{ color: '#0F1117', fontWeight: 700, fontSize: '0.95rem' }}>admin@infynihub.com</Typography>
              </Box>
            </Box>
          </Grid>

          {/* Right */}
          <Grid item xs={12} md={7}>
            <Box component="form" onSubmit={handleSubmit} sx={{ bgcolor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '20px', p: { xs: 3, md: 5 } }}>
              <Typography sx={{ fontWeight: 800, color: '#0F1117', fontSize: '1.15rem', mb: 3, letterSpacing: '-0.01em' }}>Send us a message</Typography>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Your Name" required value={formData.name} onChange={set('name')} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Work Email" type="email" required value={formData.email} onChange={set('email')} sx={fieldSx} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Company Name" value={formData.company} onChange={set('company')} sx={fieldSx} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="What are you looking to solve?" multiline rows={4} required value={formData.message} onChange={set('message')} sx={fieldSx} />
                </Grid>
                <Grid item xs={12}>
                  <Button type="submit" variant="contained" size="large" disabled={sending} endIcon={<ArrowForwardIcon />} fullWidth sx={{
                    bgcolor: '#6366F1', py: 1.8, fontWeight: 700, fontSize: '1rem',
                    borderRadius: '10px', textTransform: 'none',
                    boxShadow: '0 4px 20px rgba(99,102,241,0.3)',
                    '&:hover': { bgcolor: '#5254CC', transform: 'translateY(-2px)', boxShadow: '0 10px 32px rgba(99,102,241,0.45)' },
                    transition: 'all 0.25s ease',
                  }}>
                    {sending ? 'Sending...' : 'Send Message'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Contact;
